import { QuestionChange } from '@/client/courses';
import { InlineEditableField } from '@/components/courses/InlineEditableField';
import {
  HighlightedHtml,
  HighlightedText,
} from '@/components/text-highlighting';
import { extractTextFromHtml } from '@/components/text-highlighting/utils';
import { RichTextRenderer } from '@/ui/rich-text-renderer/RichTextRenderer';
import { QuizIcon } from '@/utils/helpers';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionIconButton } from './ActionIconButton';
import {
  AnswerOption,
  ChevronIcon,
  ComparisonBox,
  ComparisonContent,
  ComparisonLabel,
  ComparisonRow,
  ContentHeader,
  ContentHeaderLeft,
  ContentIcon,
  ContentSection,
  ContentTitle,
  HeaderButtonContainer,
  LabelText,
  ModificationBadge,
  SlideHeader,
  SlideHeaderLeft,
  SlideItemContainer,
  SlideTitle,
} from './course-changes.styles';
import { PendingQuestionEdit } from './course-changes.types';
import { countQuestionChanges } from './course-changes.utils';

export type QuestionChangesDisplayProps = {
  questionChanges: QuestionChange[];
  contentTitle: string;
  modificationCount: number;
  className?: string;
  contentIndex: number;
  onQuestionEdit: (edit: PendingQuestionEdit) => void;
  pendingEdits: PendingQuestionEdit[];
};

export const QuestionChangesDisplay: React.FC<QuestionChangesDisplayProps> = ({
  questionChanges,
  contentTitle,
  modificationCount,
  className,
  contentIndex,
  onQuestionEdit,
  pendingEdits,
}) => {
  const [expandedContent, setExpandedContent] = useState(false);
  const [expandedQuestions, setExpandedQuestions] = useState<Set<number>>(
    new Set(),
  );
  const { t } = useTranslation();
  const [editingField, setEditingField] = useState<{
    questionIndex: number;
    field:
      | 'text'
      | 'explanation'
      | 'answer'
      | 'ieSender'
      | 'ieMessage'
      | 'ieHeader'
      | 'ieBody';
    answerIndex?: number;
  } | null>(null);
  const [editHandlers, setEditHandlers] = useState<{
    onSave: () => void;
    onCancel: () => void;
  } | null>(null);

  const toggleQuestion = (index: number) => {
    const newExpanded = new Set(expandedQuestions);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedQuestions(newExpanded);
  };

  const handleCancelEdit = () => {
    if (!editHandlers) return;

    editHandlers.onCancel();
    setEditingField(null);
    setEditHandlers(null);
  };

  const handleConfirmEdit = () => {
    if (!editHandlers) return;

    editHandlers.onSave();
    setEditingField(null);
    setEditHandlers(null);
  };

  return (
    <ContentSection className={className}>
      <ContentHeader onClick={() => setExpandedContent(!expandedContent)}>
        <ContentHeaderLeft>
          <ContentIcon>
            <QuizIcon />
          </ContentIcon>
          <ContentTitle>{contentTitle}</ContentTitle>
          <ModificationBadge>
            {modificationCount} questions modified
          </ModificationBadge>
        </ContentHeaderLeft>
        <ChevronIcon className={expandedContent ? 'expanded' : ''}>
          <i className="pi pi-chevron-down" />
        </ChevronIcon>
      </ContentHeader>

      {expandedContent &&
        questionChanges.map((question, index) => {
          const changeCount = countQuestionChanges(question);
          return (
            <SlideItemContainer key={index}>
              <SlideHeader onClick={() => toggleQuestion(index)}>
                <SlideHeaderLeft>
                  <SlideTitle>Question {index + 1}</SlideTitle>
                  <ModificationBadge>
                    {changeCount} {changeCount === 1 ? 'change' : 'changes'}
                  </ModificationBadge>
                </SlideHeaderLeft>
                <ChevronIcon
                  className={expandedQuestions.has(index) ? 'expanded' : ''}
                >
                  <i className="pi pi-chevron-down" />
                </ChevronIcon>
              </SlideHeader>

              {expandedQuestions.has(index) && (
                <>
                  {/* Question Text */}
                  {(question.originalText || question.customizedText) && (
                    <ComparisonRow>
                      <ComparisonBox type="original">
                        <ComparisonLabel type="original">
                          Original question text
                        </ComparisonLabel>
                        <ComparisonContent className="html-content">
                          {question.originalText ? (
                            <RichTextRenderer content={question.originalText} />
                          ) : (
                            <div style={{ fontStyle: 'italic' }}>None</div>
                          )}
                        </ComparisonContent>
                      </ComparisonBox>
                      <ComparisonBox type="customized">
                        <ComparisonLabel type="customized">
                          <LabelText>Customized question text</LabelText>
                          {editingField?.questionIndex === index &&
                          editingField?.field === 'text' &&
                          editHandlers ? (
                            <HeaderButtonContainer>
                              <ActionIconButton
                                onClick={handleCancelEdit}
                                aria-label={t('generic.cancelEdit')}
                                icon="cancel"
                              />
                              <ActionIconButton
                                onClick={handleConfirmEdit}
                                aria-label={t('generic.confirmEdit')}
                                icon="confirm"
                              />
                            </HeaderButtonContainer>
                          ) : (
                            <ActionIconButton
                              onClick={() => {
                                setEditingField({
                                  questionIndex: index,
                                  field: 'text',
                                });
                              }}
                              aria-label={t('generic.edit')}
                              icon="edit"
                            />
                          )}
                        </ComparisonLabel>
                        <ComparisonContent className="html-content">
                          <InlineEditableField
                            value={
                              (pendingEdits.find(
                                (e) =>
                                  e.contentIndex === contentIndex &&
                                  e.questionIndex === index &&
                                  e.field === 'text',
                              )?.value as string | undefined) ??
                              question.customizedText ??
                              null
                            }
                            onSave={(value) => {
                              onQuestionEdit({
                                contentIndex,
                                questionIndex: index,
                                field: 'text',
                                value,
                              });
                              setEditingField(null);
                              setEditHandlers(null);
                            }}
                            onCancel={() => {
                              setEditingField(null);
                              setEditHandlers(null);
                            }}
                            placeholder={t(
                              'course.changes.placeholderQuestionText',
                            )}
                            renderButtonsInHeader={true}
                            isEditing={
                              editingField?.questionIndex === index &&
                              editingField?.field === 'text'
                            }
                            onHandlersReady={(handlers) => {
                              setEditHandlers({
                                onSave: () => {
                                  handlers.onSave();
                                },
                                onCancel: () => {
                                  handlers.onCancel();
                                },
                              });
                            }}
                          >
                            {question.customizedText ? (
                              <HighlightedText
                                newText={extractTextFromHtml(
                                  (pendingEdits.find(
                                    (e) =>
                                      e.contentIndex === contentIndex &&
                                      e.questionIndex === index &&
                                      e.field === 'text',
                                  )?.value as string | undefined) ??
                                    (question.customizedText || ''),
                                )}
                                originalText={extractTextFromHtml(
                                  question.originalText || '',
                                )}
                              />
                            ) : (
                              <div style={{ fontStyle: 'italic' }}>None</div>
                            )}
                          </InlineEditableField>
                        </ComparisonContent>
                      </ComparisonBox>
                    </ComparisonRow>
                  )}

                  {/* Answer Options */}
                  {(question.originalAnswers || question.customizedAnswers) && (
                    <ComparisonRow>
                      <ComparisonBox type="original">
                        <ComparisonLabel type="original">
                          Original answer options
                        </ComparisonLabel>
                        <ComparisonContent>
                          {question.originalAnswers?.map((answer, idx) => (
                            <AnswerOption
                              key={idx}
                              isCorrect={question.originalCorrectAnswers?.[idx]}
                            >
                              <div className="answer-content">{answer}</div>
                            </AnswerOption>
                          )) || <div style={{ fontStyle: 'italic' }}>None</div>}
                        </ComparisonContent>
                      </ComparisonBox>
                      <ComparisonBox type="customized">
                        <ComparisonLabel type="customized">
                          <LabelText>Customized answer options</LabelText>
                          {editingField?.questionIndex === index &&
                          editingField?.field === 'answer' &&
                          editingField?.answerIndex !== undefined &&
                          editHandlers ? (
                            <HeaderButtonContainer>
                              <ActionIconButton
                                onClick={handleCancelEdit}
                                aria-label={t('generic.cancelEdit')}
                                icon="cancel"
                              />
                              <ActionIconButton
                                onClick={handleConfirmEdit}
                                aria-label={t('generic.confirmEdit')}
                                icon="confirm"
                              />
                            </HeaderButtonContainer>
                          ) : null}
                        </ComparisonLabel>
                        <ComparisonContent>
                          {question.customizedAnswers?.map((answer, idx) => {
                            const pendingAnswerEdit = pendingEdits.find(
                              (e) =>
                                e.contentIndex === contentIndex &&
                                e.questionIndex === index &&
                                e.field === 'answers',
                            );
                            const currentAnswers =
                              (pendingAnswerEdit?.value as
                                | string[]
                                | undefined) ??
                              question.customizedAnswers ??
                              [];
                            const currentCorrectAnswers =
                              pendingAnswerEdit?.correctAnswers ??
                              question.customizedCorrectAnswers ??
                              [];
                            const currentAnswer = currentAnswers[idx] ?? answer;

                            const isEditingThisAnswer =
                              editingField?.questionIndex === index &&
                              editingField?.field === 'answer' &&
                              editingField?.answerIndex === idx;

                            return (
                              <AnswerOption
                                key={idx}
                                isCorrect={currentCorrectAnswers[idx]}
                              >
                                <div className="answer-content">
                                  <InlineEditableField
                                    value={currentAnswer}
                                    onSave={(value) => {
                                      // Create a copy of current answers and update the specific one
                                      const updatedAnswers = [
                                        ...currentAnswers,
                                      ];
                                      updatedAnswers[idx] = value;
                                      onQuestionEdit({
                                        contentIndex,
                                        questionIndex: index,
                                        field: 'answers',
                                        value: updatedAnswers,
                                        correctAnswers: currentCorrectAnswers,
                                      });
                                      setEditingField(null);
                                      setEditHandlers(null);
                                    }}
                                    onCancel={() => {
                                      setEditingField(null);
                                      setEditHandlers(null);
                                    }}
                                    placeholder={t(
                                      'course.changes.placeholderAnswerOption',
                                    )}
                                    renderButtonsInHeader={true}
                                    isEditing={isEditingThisAnswer}
                                    onHandlersReady={(handlers) => {
                                      setEditHandlers({
                                        onSave: () => {
                                          handlers.onSave();
                                        },
                                        onCancel: () => {
                                          handlers.onCancel();
                                        },
                                      });
                                    }}
                                  >
                                    <HighlightedText
                                      newText={currentAnswer}
                                      originalText={
                                        question.originalAnswers?.[idx] || ''
                                      }
                                    />
                                  </InlineEditableField>
                                </div>
                                {!isEditingThisAnswer && (
                                  <ActionIconButton
                                    onClick={() => {
                                      setEditingField({
                                        questionIndex: index,
                                        field: 'answer',
                                        answerIndex: idx,
                                      });
                                    }}
                                    aria-label={t('generic.edit')}
                                    icon="edit"
                                  />
                                )}
                              </AnswerOption>
                            );
                          }) || <div style={{ fontStyle: 'italic' }}>None</div>}
                        </ComparisonContent>
                      </ComparisonBox>
                    </ComparisonRow>
                  )}

                  {/* Explanation */}
                  {(question.originalExplanation ||
                    question.customizedExplanation) && (
                    <ComparisonRow>
                      <ComparisonBox type="original">
                        <ComparisonLabel type="original">
                          Original explanation
                        </ComparisonLabel>
                        <ComparisonContent className="html-content">
                          {question.originalExplanation ? (
                            <RichTextRenderer
                              content={question.originalExplanation}
                            />
                          ) : (
                            <div style={{ fontStyle: 'italic' }}>None</div>
                          )}
                        </ComparisonContent>
                      </ComparisonBox>
                      <ComparisonBox type="customized">
                        <ComparisonLabel type="customized">
                          <LabelText>Customized explanation</LabelText>
                          {editingField?.questionIndex === index &&
                          editingField?.field === 'explanation' &&
                          editHandlers ? (
                            <HeaderButtonContainer>
                              <ActionIconButton
                                onClick={handleCancelEdit}
                                aria-label={t('generic.cancelEdit')}
                                icon="cancel"
                              />
                              <ActionIconButton
                                onClick={handleConfirmEdit}
                                aria-label={t('generic.confirmEdit')}
                                icon="confirm"
                              />
                            </HeaderButtonContainer>
                          ) : (
                            <ActionIconButton
                              onClick={() => {
                                setEditingField({
                                  questionIndex: index,
                                  field: 'explanation',
                                });
                              }}
                              aria-label={t('generic.edit')}
                              icon="edit"
                            />
                          )}
                        </ComparisonLabel>
                        <ComparisonContent className="html-content">
                          <InlineEditableField
                            value={
                              (pendingEdits.find(
                                (e) =>
                                  e.contentIndex === contentIndex &&
                                  e.questionIndex === index &&
                                  e.field === 'explanation',
                              )?.value as string | undefined) ??
                              question.customizedExplanation ??
                              null
                            }
                            onSave={(value) => {
                              onQuestionEdit({
                                contentIndex,
                                questionIndex: index,
                                field: 'explanation',
                                value,
                              });
                              setEditingField(null);
                              setEditHandlers(null);
                            }}
                            onCancel={() => {
                              setEditingField(null);
                              setEditHandlers(null);
                            }}
                            placeholder={t(
                              'course.changes.placeholderExplanation',
                            )}
                            renderButtonsInHeader={true}
                            isEditing={
                              editingField?.questionIndex === index &&
                              editingField?.field === 'explanation'
                            }
                            onHandlersReady={(handlers) => {
                              setEditHandlers({
                                onSave: () => {
                                  handlers.onSave();
                                },
                                onCancel: () => {
                                  handlers.onCancel();
                                },
                              });
                            }}
                          >
                            {question.customizedExplanation ? (
                              <HighlightedText
                                newText={extractTextFromHtml(
                                  (pendingEdits.find(
                                    (e) =>
                                      e.contentIndex === contentIndex &&
                                      e.questionIndex === index &&
                                      e.field === 'explanation',
                                  )?.value as string | undefined) ??
                                    (question.customizedExplanation || ''),
                                )}
                                originalText={extractTextFromHtml(
                                  question.originalExplanation || '',
                                )}
                              />
                            ) : (
                              <div style={{ fontStyle: 'italic' }}>None</div>
                            )}
                          </InlineEditableField>
                        </ComparisonContent>
                      </ComparisonBox>
                    </ComparisonRow>
                  )}
                  {/* Interactive element (SMS/email) */}
                  {(() => {
                    const ie = question.interactiveElementChange;

                    if (!ie) return null;

                    const isSms = ie.type === 'sms';
                    type IeField =
                      | 'ieSender'
                      | 'ieMessage'
                      | 'ieHeader'
                      | 'ieBody';
                    const ieFields: {
                      field: IeField;
                      original: string | null | undefined;
                      customized: string | null | undefined;
                      originalKey: string;
                      customizedKey: string;
                      placeholder: string;
                      show: boolean;
                      isHtml: boolean;
                    }[] = [
                      {
                        field: 'ieSender',
                        original: ie.originalSender,
                        customized: ie.customizedSender,
                        originalKey: 'course.changes.originalInteractiveSender',
                        customizedKey:
                          'course.changes.customizedInteractiveSender',
                        placeholder: t('course.changes.placeholderSender'),
                        show: !!(ie.originalSender || ie.customizedSender),
                        isHtml: false,
                      },
                      {
                        field: 'ieMessage',
                        original: ie.originalMessage,
                        customized: ie.customizedMessage,
                        originalKey:
                          'course.changes.originalInteractiveMessage',
                        customizedKey:
                          'course.changes.customizedInteractiveMessage',
                        placeholder: t('course.changes.placeholderMessage'),
                        show:
                          isSms &&
                          !!(ie.originalMessage || ie.customizedMessage),
                        isHtml: true,
                      },
                      {
                        field: 'ieHeader',
                        original: ie.originalHeader,
                        customized: ie.customizedHeader,
                        originalKey: 'course.changes.originalInteractiveHeader',
                        customizedKey:
                          'course.changes.customizedInteractiveHeader',
                        placeholder: t('course.changes.placeholderEmailHeader'),
                        show:
                          !isSms &&
                          !!(ie.originalHeader || ie.customizedHeader),
                        isHtml: true,
                      },
                      {
                        field: 'ieBody',
                        original: ie.originalBody,
                        customized: ie.customizedBody,
                        originalKey: 'course.changes.originalInteractiveBody',
                        customizedKey:
                          'course.changes.customizedInteractiveBody',
                        placeholder: t('course.changes.placeholderEmailBody'),
                        show:
                          !isSms && !!(ie.originalBody || ie.customizedBody),
                        isHtml: true,
                      },
                    ];
                    return ieFields
                      .filter((f) => f.show)
                      .map(
                        ({
                          field,
                          original,
                          customized,
                          originalKey,
                          customizedKey,
                          placeholder,
                          isHtml: fieldIsHtml,
                        }) => (
                          <ComparisonRow key={field}>
                            <ComparisonBox type="original">
                              <ComparisonLabel type="original">
                                {t(originalKey)}
                              </ComparisonLabel>
                              <ComparisonContent
                                className={
                                  fieldIsHtml ? 'html-content' : undefined
                                }
                              >
                                {original ? (
                                  fieldIsHtml ? (
                                    <RichTextRenderer content={original} />
                                  ) : (
                                    original
                                  )
                                ) : (
                                  t('generic.none')
                                )}
                              </ComparisonContent>
                            </ComparisonBox>
                            <ComparisonBox type="customized">
                              <ComparisonLabel type="customized">
                                <LabelText>{t(customizedKey)}</LabelText>
                                {editingField?.questionIndex === index &&
                                editingField?.field === field &&
                                editHandlers ? (
                                  <HeaderButtonContainer>
                                    <ActionIconButton
                                      onClick={handleCancelEdit}
                                      aria-label={t('generic.cancelEdit')}
                                      icon="cancel"
                                    />
                                    <ActionIconButton
                                      onClick={handleConfirmEdit}
                                      aria-label={t('generic.confirmEdit')}
                                      icon="confirm"
                                    />
                                  </HeaderButtonContainer>
                                ) : (
                                  <ActionIconButton
                                    onClick={() =>
                                      setEditingField({
                                        questionIndex: index,
                                        field,
                                      })
                                    }
                                    aria-label={t('generic.edit')}
                                    icon="edit"
                                  />
                                )}
                              </ComparisonLabel>
                              <ComparisonContent
                                className={
                                  fieldIsHtml ? 'html-content' : undefined
                                }
                              >
                                <InlineEditableField
                                  value={
                                    (pendingEdits.find(
                                      (e) =>
                                        e.contentIndex === contentIndex &&
                                        e.questionIndex === index &&
                                        e.field === field,
                                    )?.value as string | undefined) ??
                                    customized ??
                                    null
                                  }
                                  onSave={(value) => {
                                    onQuestionEdit({
                                      contentIndex,
                                      questionIndex: index,
                                      field,
                                      value,
                                    });
                                    setEditingField(null);
                                    setEditHandlers(null);
                                  }}
                                  onCancel={() => {
                                    setEditingField(null);
                                    setEditHandlers(null);
                                  }}
                                  isHtml={fieldIsHtml}
                                  placeholder={placeholder}
                                  renderButtonsInHeader={true}
                                  isEditing={
                                    editingField?.questionIndex === index &&
                                    editingField?.field === field
                                  }
                                  onHandlersReady={(handlers) => {
                                    setEditHandlers({
                                      onSave: () => handlers.onSave(),
                                      onCancel: () => handlers.onCancel(),
                                    });
                                  }}
                                >
                                  {customized ? (
                                    fieldIsHtml ? (
                                      <HighlightedHtml
                                        originalHtml={original || ''}
                                        newHtml={
                                          (pendingEdits.find(
                                            (e) =>
                                              e.contentIndex === contentIndex &&
                                              e.questionIndex === index &&
                                              e.field === field,
                                          )?.value as string | undefined) ??
                                          customized
                                        }
                                      />
                                    ) : (
                                      <HighlightedText
                                        originalText={original || ''}
                                        newText={
                                          (pendingEdits.find(
                                            (e) =>
                                              e.contentIndex === contentIndex &&
                                              e.questionIndex === index &&
                                              e.field === field,
                                          )?.value as string | undefined) ??
                                          customized
                                        }
                                      />
                                    )
                                  ) : (
                                    t('generic.none')
                                  )}
                                </InlineEditableField>
                              </ComparisonContent>
                            </ComparisonBox>
                          </ComparisonRow>
                        ),
                      );
                  })()}
                </>
              )}
            </SlideItemContainer>
          );
        })}
    </ContentSection>
  );
};
